'use client';

import { useEffect } from 'react';

/**
 * Timestamp guard in sessionStorage rather than a boolean: it blocks a reload
 * loop while the failure persists, but lets a fresh incident hours later (same
 * tab, another deploy) auto-recover again.
 */
const RELOAD_GUARD_KEY = 'chunk-load-reload-at';
const RELOAD_LOOP_WINDOW_MS = 30_000;

function isChunkLoadError(error: Error) {
	return (
		error.name === 'ChunkLoadError' ||
		/ChunkLoadError|Failed to load chunk|Loading chunk .+ failed|dynamically imported module|Importing a module script failed/i.test(
			error.message,
		)
	);
}

/**
 * Auto-recover from chunk-load failures caught by a route error boundary.
 *
 * These surface when a client-side navigation requests route JS/CSS that no
 * longer exists on the server — typically a tab from an old deployment
 * (outside Vercel Skew Protection's window) navigating to a route whose
 * assets 404 after a redeploy. New routes are the most exposed: no visitor
 * has their chunks in browser cache yet. A full reload always recovers
 * because the fresh document references the current deployment's assets, so
 * perform it automatically instead of stranding the visitor on the error
 * screen. Non-chunk errors are left to the boundary UI untouched.
 */
export function useChunkLoadRecovery(error: Error) {
	useEffect(() => {
		if (!isChunkLoadError(error)) return;

		try {
			const lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
			if (Date.now() - lastReloadAt < RELOAD_LOOP_WINDOW_MS) return;
			sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
		} catch {
			// Storage unavailable: reloading without loop protection is worse than
			// showing the error screen.
			return;
		}

		window.location.reload();
	}, [error]);
}
